// AdminCastPage.tsx — (B101; Creative 2 C2.6-1, planning 10-22 13:50) Admin → Cast: describe a pair of hands, a creature or
// a group; the engine makes four candidates (front, angle, in use); a person picks one. Nothing is used before the pick
// (binding a picked entity to a video is C2.6-2's). The heading and note are the owner's approved words (10-22 14:05).
//   /admin/cast        the form, and the sets seen on this browser
//   /admin/cast/:id    one set: read every 10 s while it's being made; the four candidates; Pick this one
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageContainer, TfCard, Btn } from "@/components/v2/primitives.tsx";
import { castApi } from "@/api/cast.ts";
import { seriesHistory, rememberSeries } from "@/lib/series.ts";
import { castTitle, CAST_AI_NOTE, CAST_KINDS, CAST_STYLES, VIEWS, VIEW_LABEL, CAST_COST_NOTE, CAST_HISTORY_KEY, CAST_POLL_MS, castBody, castState, castPollNext, pickOffer,
  pickedOf, castRefusalText, type CastForm, type CastSet } from "@/lib/cast.ts";

const storage = () => { try { return window.localStorage; } catch { return null; } };   // catch-reason: no storage: no history kept
const field = { display: "block", width: "100%", marginTop: 4 } as const;

function NewCast() {
  const nav = useNavigate();
  const [f, setF] = useState<CastForm>({ entityKind: "hands", name: "", description: "", groupCount: "", style: "photoreal" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const b = castBody(f);
  const go = async () => {
    if (!("body" in b)) return;
    setBusy(true); setErr(null);
    try { const r = await castApi.create(b.body); rememberSeries(storage(), { id: r.id, title: b.body.name }, new Date(), CAST_HISTORY_KEY); nav(`/admin/cast/${r.id}`); }
    catch (e) { setErr(castRefusalText(e)); }   // catch-reason: shown under the button; nothing was made
    finally { setBusy(false); }
  };
  return (
    <TfCard>
      <h3 style={{ marginTop: 0 }}>New candidates</h3>
      <label style={{ display: "block" }}>What it is
        <select style={field} value={f.entityKind} onChange={(e) => setF({ ...f, entityKind: e.target.value as CastForm["entityKind"] })}>
          {CAST_KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}</select></label>
      <label style={{ display: "block", marginTop: 8 }}>Its name<input style={field} maxLength={60} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
      <label style={{ display: "block", marginTop: 8 }}>Describe it (no real person, brand or place)
        <textarea style={field} rows={3} maxLength={400} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></label>
      {f.entityKind === "group" && <label style={{ display: "block", marginTop: 8 }}>How many in the group (2–12)
        <input style={field} type="number" min={2} max={12} value={f.groupCount} onChange={(e) => setF({ ...f, groupCount: e.target.value })} /></label>}
      <label style={{ display: "block", marginTop: 8 }}>Style
        <select style={field} value={f.style} onChange={(e) => setF({ ...f, style: e.target.value })}>
          {CAST_STYLES.map((s) => <option key={s.style} value={s.style}>{s.label}</option>)}</select></label>
      <div className="tf-muted" style={{ fontSize: 12, marginTop: 8 }}>{CAST_COST_NOTE}</div>
      {"off" in b && (f.name || f.description) && <div style={{ fontSize: 12, color: "#92400e", marginTop: 4 }}>{b.off}</div>}
      {err && <div style={{ color: "#b91c1c", marginTop: 6 }}>{err}</div>}
      <div style={{ marginTop: 10 }}><Btn onClick={go} disabled={busy || !("body" in b)}>{busy ? "Asking…" : "Make four candidates"}</Btn></div>
    </TfCard>
  );
}

function CastSetView({ id }: { id: string }) {
  const [s, setS] = useState<CastSet | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pickErr, setPickErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [seq, setSeq] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const started = useRef(Date.now());
  const last = useRef<CastSet["status"]>("generating");   // (audit 10-23) a failed read keeps polling at the last status known
  useEffect(() => {
    let alive = true, t: ReturnType<typeof setTimeout> | undefined;
    started.current = Date.now(); setTimedOut(false); last.current = "generating";
    const tick = async () => {
      let r: CastSet | null = null;
      try { r = await castApi.get(id); if (!alive) return; setS(r); setErr(null); last.current = r.status; rememberSeries(storage(), { id, title: r.name }, new Date(), CAST_HISTORY_KEY); }
      catch (e) { if (alive) setErr(castRefusalText(e)); }   // catch-reason: shown in place of the set; read again below while it's being made
      if (!alive) return;
      const next = castPollNext(r?.status ?? last.current, started.current, Date.now());
      if (next === "poll") t = setTimeout(tick, CAST_POLL_MS); else if (next === "timeout") setTimedOut(true);
    };
    void tick();
    return () => { alive = false; clearTimeout(t); };
  }, [id, seq]);
  const pick = async (c: number) => {
    if (!s || !window.confirm(`Pick candidate ${c + 1}? The other three aren't used.`)) return;
    setBusy(true); setPickErr(null);
    try { await castApi.pick(id, c); setSeq((n) => n + 1); }
    catch (e) { setPickErr(castRefusalText(e)); }   // catch-reason: shown above the candidates; nothing changed
    finally { setBusy(false); }
  };
  if (err && !s) return <TfCard><div style={{ color: "#b91c1c" }}>The candidates couldn't be read: {err}</div><Btn size="sm" variant="ghost" onClick={() => setSeq((n) => n + 1)}>Read again</Btn></TfCard>;
  if (!s) return <TfCard><div className="tf-muted">Reading…</div></TfCard>;
  const picked = pickedOf(s);
  return (
    <TfCard>
      <h3 style={{ marginTop: 0 }}>{castTitle(s.entityKind)}</h3>
      <div className="tf-muted" style={{ fontSize: 13 }}>{CAST_AI_NOTE}</div>
      <div style={{ fontSize: 13, marginTop: 6 }}><strong>{s.name}</strong> · {s.entityKind}{s.groupCount ? ` of ${s.groupCount}` : ""} · {s.style} — {s.description}</div>
      <div style={{ fontSize: 13, marginTop: 6 }}>{castState(s)}</div>
      {timedOut && <div style={{ fontSize: 12, color: "#92400e" }}>Still being made after 20 minutes: <Btn size="sm" variant="ghost" onClick={() => setSeq((n) => n + 1)}>Read again</Btn></div>}
      {err && <div style={{ fontSize: 12, color: "#b91c1c" }}>The candidates couldn't be read again: {err}</div>}
      {pickErr && <div style={{ color: "#b91c1c", marginTop: 6 }}>{pickErr}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginTop: 12 }}>
        {s.candidates.map((c) => { const o = pickOffer(s, c.candidate); return (
          <div key={c.candidate} style={{ border: `2px solid ${picked === c.candidate ? "#4f46e5" : "#e5e7eb"}`, borderRadius: 8, padding: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Candidate {c.candidate + 1}{picked === c.candidate ? " — picked" : ""}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4, marginTop: 6 }}>
              {VIEWS.map((v) => { const im = c.images.find((x) => x.view === v); return (
                <figure key={v} style={{ margin: 0 }}>
                  {im ? <img src={im.url} alt={`Candidate ${c.candidate + 1}, ${VIEW_LABEL[v]}`} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 4 }} />
                    : <div style={{ aspectRatio: "1", background: "#f3f4f6", borderRadius: 4 }} />}
                  <figcaption className="tf-muted" style={{ fontSize: 11 }}>{VIEW_LABEL[v]}{im ? "" : ": not made"}</figcaption>
                </figure>); })}
            </div>
            {o.show && <div style={{ marginTop: 6 }}><Btn size="sm" onClick={() => pick(c.candidate)} disabled={busy || !o.enabled}>Pick this one</Btn>
              {o.note && <div className="tf-muted" style={{ fontSize: 11 }}>{o.note}</div>}</div>}
          </div>); })}
      </div>
    </TfCard>
  );
}

export function AdminCastPage() {
  const { id } = useParams<{ id?: string }>();
  const seen = seriesHistory(storage(), CAST_HISTORY_KEY);
  return (
    <PageContainer title="Cast" subtitle="AI-made candidates for a pair of hands, a creature or a group in your series: four to choose from."
      action={id ? <Link to="/admin/cast"><Btn variant="ghost">All candidates</Btn></Link> : undefined}>
      {id ? <CastSetView id={id} /> : <>
        <NewCast />
        {seen.length > 0 && <TfCard>
          <h3 style={{ marginTop: 0 }}>Seen on this browser</h3>
          <ul style={{ fontSize: 13, paddingLeft: 18 }}>{seen.map((x) => <li key={x.id}><Link to={`/admin/cast/${x.id}`}>{x.title}</Link> <span className="tf-muted">{x.at.slice(0, 16).replace("T", " ")}</span></li>)}</ul>
        </TfCard>}
      </>}
    </PageContainer>
  );
}
